/**
 * Theme toggle — single icon button that cycles light → dark → system.
 *
 * Compact and icon-only so it fits in the sidebar footer or top-right of
 * pages without chrome. The current state is shown by the icon:
 *   Sun     → light
 *   Moon    → dark
 *   Monitor → system (default)
 * Hover tooltip and the aria-label announce both the current mode and what
 * clicking will do next.
 */

import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type Theme } from "../lib/theme";

const NEXT: Record<Theme, Theme> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const LABEL: Record<Theme, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

const ICON: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export function ThemeToggle({ className = "" }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const Icon = ICON[theme];
  const next = NEXT[theme];

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title={`Theme: ${LABEL[theme]} — click for ${LABEL[next]}`}
      aria-label={`Theme: ${LABEL[theme]}. Click to switch to ${LABEL[next]}.`}
      className={[
        "inline-flex items-center justify-center rounded-md p-1.5",
        "text-muted-foreground hover:text-foreground hover:bg-accent",
        "transition-colors",
        className,
      ].join(" ")}
    >
      <Icon className="size-4" aria-hidden="true" />
    </button>
  );
}
